import type { ReactNode } from 'react';

import { useEnrolledProducts } from '~/hooks/useEnrolledProducts';

import { INPUT_SOURCES } from '../constants/connector-sources';
import { useConnectedConnectors } from '../hooks/use-connections';

import { ConnectChannelPlaceholder } from './connect-channel-placeholder';

interface RequireChannelConnectionProps {
  children: ReactNode;
  /** Optional override for the placeholder's body copy (e.g. "to run experiments"). */
  description?: string;
}

/**
 * Page-level gate for routes that require a connected catalog source
 * (catalog-operations + experiments/create). Allows the page through iff the
 * tenant has at least one connector from `INPUT_SOURCES` (GMC, Shopify, or
 * Manual Upload). Loading state defaults to "render children" so the cold
 * load doesn't flash the placeholder for users who actually do have a
 * connection.
 *
 * Trial users (any platform with a TRIAL_POC contract) are always let through —
 * their catalog data flows through the trial import pipeline rather than a
 * traditional connected channel.
 *
 * Replaces an earlier check against the backend's `connectedToChannel`
 * boolean, which counted *any* connected channel (including ads/analytics-
 * only tenants) and so let users into catalog-operations without an actual
 * catalog source.
 */
export function RequireChannelConnection({ children, description }: RequireChannelConnectionProps) {
  const { data, isLoading, isError } = useConnectedConnectors();
  const { data: enrolledProducts } = useEnrolledProducts();
  // Trial users get direct access — their catalog is managed through the trial
  // import flow, not a traditional INPUT_SOURCE connector.
  const isAnyTrial = enrolledProducts?.some((p) => p.contractType === 'TRIAL_POC') ?? false;
  // Treat loading + error as "let through" — the page or its inner queries
  // will surface the real failure, and we never want a transient network
  // hiccup to bounce a connected user to the placeholder.
  if (!isLoading && !isError && data && !isAnyTrial) {
    const hasInputSource = data.some((c) => INPUT_SOURCES.includes(c.code));
    if (!hasInputSource) {
      return (
        <ConnectChannelPlaceholder
          testId="connect-channel-placeholder"
          {...(description ? { description } : {})}
        />
      );
    }
  }
  return <>{children}</>;
}
